import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { PersonRequestHistoryItem } from "../../api/requestsAdmin";
import { AwaitingDependenciesStatus, formatDate, shortEmail } from "./requestHistoryFormat";
import styles from "./RequestTimeline.module.css";

/** How a step on the timeline is drawn: done, the step it is on now, refused, or set aside. */
type StepTone = "done" | "current" | "refused" | "neutral";

/** One step of a request's history: what happened, when, and by whom where that is known. */
interface TimelineStep {
  key: string;
  label: string;
  when: string | null;
  who: string | null;
  tone: StepTone;
}

/**
 * The steps of a request's history that the request itself records: when it was raised, how and when it was decided and
 * by whom, and, while it is still open, what it is waiting for. Nothing is shown that the row does not hold: the request
 * keeps one decision time, so an approval that waited on other requests shows the time it went through, not the earlier
 * time the approver said yes (issue #130 covers a fuller history).
 *
 * @param item - The request.
 * @param t - The translation function.
 * @returns The steps, oldest first.
 */
function timelineSteps(item: PersonRequestHistoryItem, t: TFunction): TimelineStep[] {
  const who = item.decidedByName ?? shortEmail(item.decidedByEmail);
  const steps: TimelineStep[] = [{ key: "raised", label: t("myRequests.timeline.raised"), when: item.createdAt, who: null, tone: "done" }];
  const decided = (key: string, label: string, tone: StepTone): TimelineStep => ({ key, label, when: item.decidedAt, who, tone });

  switch (item.status) {
    case "Pending":
      steps.push({ key: "waiting", label: t("myRequests.timeline.waiting"), when: null, who: null, tone: "current" });
      break;
    case "AutoApproved":
      steps.push({ key: "decided", label: t("myRequests.timeline.autoApproved"), when: item.decidedAt, who: null, tone: "done" });
      break;
    case "Approved":
      steps.push(decided("decided", t("myRequests.timeline.approved"), "done"));
      break;
    case AwaitingDependenciesStatus:
      steps.push(decided("decided", t("myRequests.timeline.approved"), "done"));
      steps.push({ key: "setup", label: t("myRequests.timeline.settingUp"), when: null, who: null, tone: "current" });
      break;
    case "Completed":
      steps.push(decided("decided", t("myRequests.timeline.completed"), "done"));
      break;
    case "Denied":
      steps.push(decided("decided", t("myRequests.timeline.denied"), "refused"));
      break;
    case "Withdrawn":
      steps.push({ key: "decided", label: t("myRequests.timeline.withdrawn"), when: item.decidedAt, who: null, tone: "neutral" });
      break;
    default:
      // A status this page does not know yet: say what it is rather than guess at a history.
      steps.push({ key: "status", label: item.status, when: item.decidedAt, who, tone: "current" });
  }

  return steps;
}

/**
 * A request's history as a timeline: a heading, then each step as a dot on a line, oldest first. Vertical in the tiles'
 * tooltip; horizontal in the table's expanded row, where there is width to spare and little height.
 *
 * @param props.item - The request.
 * @param props.orientation - Which way the steps run.
 */
export function RequestTimeline({ item, orientation }: { item: PersonRequestHistoryItem; orientation: "vertical" | "horizontal" }): React.JSX.Element {
  const { t } = useTranslation();
  const steps = timelineSteps(item, t);

  return (
    <div className={orientation === "horizontal" ? `${styles.timeline} ${styles.horizontal}` : styles.timeline}>
      <p className={orientation === "horizontal" ? `panel-title ${styles.heading}` : styles.heading}>{t("myRequests.timeline.title")}</p>
      <ol className={styles.steps}>
        {steps.map((step) => (
          <li key={step.key} className={`${styles.step} ${styles[step.tone]}`}>
            <span className={styles.dot} aria-hidden="true" />
            <span className={styles.label}>{step.label}</span>
            {step.when !== null && <span className={styles.meta}>{formatDate(step.when)}</span>}
            {step.who !== null && <span className={styles.meta}>{t("myRequests.timeline.by", { name: step.who })}</span>}
            {step.tone === "current" && <span className={styles.meta}>{t("myRequests.timeline.now")}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * A request's "Logged …" or "Completed … by …" line, which opens the request's timeline beneath it on hover, on focus
 * (it can be tabbed to, and tapped on a touch screen) and closes again on Escape. The timeline is the line's description
 * for a screen reader, so it is read out without being opened.
 *
 * @param props.item - The request.
 * @param props.className - The line's class, from the tile.
 * @param props.children - The line's text.
 */
export function RequestWhen({ item, className, children }: { item: PersonRequestHistoryItem; className?: string; children: string }): React.JSX.Element {
  const timelineId = useId();
  // Escape closes the timeline even while the pointer rests on it (WCAG 1.4.13); it can open again once the pointer leaves.
  const [dismissed, setDismissed] = useState(false);

  return (
    <div
      className={[styles.anchor, dismissed ? styles.dismissed : "", className ?? ""].filter(Boolean).join(" ")}
      onMouseLeave={() => setDismissed(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setDismissed(true);
        }
      }}
    >
      <span className={styles.trigger} tabIndex={0} aria-describedby={timelineId} onBlur={() => setDismissed(false)}>
        {children}
      </span>
      <div id={timelineId} role="tooltip" className={styles.tooltip}>
        <RequestTimeline item={item} orientation="vertical" />
      </div>
    </div>
  );
}
